/**
 * Firestore Conversations Service
 * 
 * Handles all conversation-related operations:
 * - Create conversations (direct & group)
 * - Fetch conversations
 * - Real-time subscriptions
 * - Pin/unpin
 * - Update group details
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  deleteField
} from 'firebase/firestore';
import { getFirestoreDb, isFirebaseSignedIn } from '../firebase';
import { COLLECTIONS, CONVERSATION_TYPES, MESSAGE_TYPES, SYSTEM_MESSAGE_TYPES, PAGE_SIZE, sanitizeAvatarUrl } from './schema';
import { addSystemMessage } from './messages';

const db = () => getFirestoreDb();

/**
 * Create a new direct conversation between two users
 * Returns existing conversation if one already exists
 */
export async function createDirectConversation(currentUser, otherUser) {
  const conversationsRef = collection(db(), COLLECTIONS.CONVERSATIONS);
  
  // Check if direct conversation already exists between these users
  const existingQuery = query(
    conversationsRef,
    where('type', '==', CONVERSATION_TYPES.DIRECT),
    where('participants', 'array-contains', currentUser.id)
  );
  
  const snapshot = await getDocs(existingQuery);
  const existing = snapshot.docs.find(doc => {
    const data = doc.data();
    return data.participants.includes(otherUser.id);
  });
  
  if (existing) {
    return { id: existing.id, ...existing.data() };
  }
  
  // Create new direct conversation
  const conversationData = {
    type: CONVERSATION_TYPES.DIRECT,
    participants: [currentUser.id, otherUser.id],
    participantDetails: {
      [currentUser.id]: {
        name: currentUser.name || currentUser.email,
        avatar: sanitizeAvatarUrl(currentUser.avatar)
      },
      [otherUser.id]: {
        name: otherUser.name || otherUser.email,
        avatar: sanitizeAvatarUrl(otherUser.avatar)
      }
    },
    name: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessage: null,
    pinnedBy: [],
    createdBy: currentUser.id
  };
  
  const docRef = await addDoc(conversationsRef, conversationData);
  return { id: docRef.id, ...conversationData };
}

/**
 * Build auto-generated group name from participant details.
 * Sorted alphabetically for deterministic output regardless of Firestore key ordering.
 * @param {Object} participantDetails - { userId: { name, avatar } }
 * @param {string|null} excludeId - participant to exclude (for remove/leave)
 */
function buildAutoName(participantDetails, excludeId = null) {
  const names = Object.entries(participantDetails)
    .filter(([id]) => id !== excludeId)
    .map(([_, p]) => p.name.split(' ')[0])
    .sort();
  return names.slice(0, 3).join(', ') + (names.length > 3 ? `, +${names.length - 3}` : '');
}

/**
 * Order-independent detection: checks if a group name is composed of
 * participant first names (in any order) rather than a custom name.
 * Handles "+N" suffix for groups with 4+ members.
 */
function isAutoGeneratedName(name, details) {
  if (!name) return false;
  const firstNames = new Set(Object.values(details).map(d => d.name.split(' ')[0]));
  const parts = name.split(', ');
  const nameParts = parts.filter(p => !p.startsWith('+'));
  const suffix = parts.find(p => p.startsWith('+'));

  if (!nameParts.every(n => firstNames.has(n))) return false;

  if (suffix) {
    const extraCount = parseInt(suffix.replace('+', ''), 10);
    if (nameParts.length + extraCount !== firstNames.size) return false;
  } else {
    if (nameParts.length !== firstNames.size) return false;
  }

  return true;
}

/**
 * Create a new group conversation
 */
export async function createGroupConversation(currentUser, participants, groupName = null) {
  const conversationsRef = collection(db(), COLLECTIONS.CONVERSATIONS);
  
  // Build participant details
  const participantDetails = {};
  const participantIds = [currentUser.id];
  
  participantDetails[currentUser.id] = {
    name: currentUser.name || currentUser.email,
    avatar: sanitizeAvatarUrl(currentUser.avatar)
  };
  
  participants.forEach(user => {
    participantIds.push(user.id);
    participantDetails[user.id] = {
      name: user.name || user.email,
      avatar: sanitizeAvatarUrl(user.avatar)
    };
  });
  
  const autoName = groupName || buildAutoName(participantDetails);
  
  const conversationData = {
    type: CONVERSATION_TYPES.GROUP,
    participants: participantIds,
    participantDetails,
    name: autoName,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessage: null,
    pinnedBy: [],
    createdBy: currentUser.id
  };
  
  const docRef = await addDoc(conversationsRef, conversationData);
  
  // Add system message about group creation
  await addSystemMessage(docRef.id, {
    subtype: SYSTEM_MESSAGE_TYPES.GROUP_CREATED,
    text: `${currentUser.name || 'Someone'} created the group`,
    userId: currentUser.id
  });
  
  return { id: docRef.id, ...conversationData };
}

/**
 * Get a single conversation by ID
 */
export async function getConversation(conversationId) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) {
    return null;
  }
  
  return { id: snapshot.id, ...snapshot.data() };
}

/**
 * Get all conversations for a user
 */
export async function getUserConversations(userId) {
  const conversationsRef = collection(db(), COLLECTIONS.CONVERSATIONS);
  const q = query(
    conversationsRef,
    where('participants', 'array-contains', userId),
    orderBy('updatedAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * Subscribe to real-time updates for user's conversations (limited for performance)
 */
export function subscribeToConversations(userId, callback, onError, limitCount = PAGE_SIZE.CONVERSATIONS) {
  if (!isFirebaseSignedIn()) {
    console.warn('[Firestore] subscribeToConversations called before Firebase Auth ready, skipping');
    if (onError) onError(new Error('Firebase Auth not ready'));
    return () => {};
  }
  const conversationsRef = collection(db(), COLLECTIONS.CONVERSATIONS);
  const q = query(
    conversationsRef,
    where('participants', 'array-contains', userId),
    orderBy('updatedAt', 'desc'),
    limit(limitCount)
  );
  
  return onSnapshot(q, (snapshot) => {
    const conversations = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(conversations, snapshot.docs[snapshot.docs.length - 1]);
  }, (error) => {
    console.error('Error subscribing to conversations:', error);
    if (onError) onError(error);
  });
}

/**
 * Load more conversations beyond the real-time subscription limit
 */
export async function getMoreConversations(userId, lastDoc, pageSize = PAGE_SIZE.CONVERSATIONS) {
  const conversationsRef = collection(db(), COLLECTIONS.CONVERSATIONS);
  const q = query(
    conversationsRef,
    where('participants', 'array-contains', userId),
    orderBy('updatedAt', 'desc'),
    startAfter(lastDoc),
    limit(pageSize)
  );
  const snapshot = await getDocs(q);
  return {
    conversations: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
    lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
    hasMore: snapshot.docs.length === pageSize
  };
}

/**
 * Update conversation's last message (called when sending a message)
 */
export async function updateLastMessage(conversationId, message) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId);
  
  // Strip HTML so the sidebar preview and mobile app show clean text
  const plainText = message.text
    ? message.text.replace(/<br\s*\/?>/gi, ' ').replace(/<\/p><p>/gi, ' ').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/\s+/g, ' ').trim()
    : '';

  await updateDoc(docRef, {
    lastMessage: {
      text: plainText || (message.type === MESSAGE_TYPES.IMAGE ? '📷 Image' : message.type === MESSAGE_TYPES.VOICE ? '🎤 Voice memo' : message.type === MESSAGE_TYPES.VIDEO ? '🎬 Video' : message.type === MESSAGE_TYPES.POLL ? '📊 Poll' : ''),
      senderId: message.senderId,
      senderName: message.senderName,
      timestamp: serverTimestamp(),
      type: message.type
    },
    updatedAt: serverTimestamp()
  });
}

/**
 * Pin a conversation for a user (with timestamp for ordering)
 */
export async function pinConversation(conversationId, userId) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) {
    throw new Error('Conversation not found');
  }
  
  const data = snapshot.data();
  const pinnedBy = data.pinnedBy || [];
  const pinnedAt = data.pinnedAt || {};
  
  // Add user to pinnedBy array and record timestamp
  if (!pinnedBy.includes(userId)) {
    await updateDoc(docRef, {
      pinnedBy: arrayUnion(userId),
      [`pinnedAt.${userId}`]: new Date().toISOString()
    });
  }
}

/**
 * Unpin a conversation for a user
 */
export async function unpinConversation(conversationId, userId) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId);
  await updateDoc(docRef, {
    pinnedBy: arrayRemove(userId),
    [`pinnedAt.${userId}`]: null
  });
}

/**
 * Mark a conversation as read for a user
 */
export async function markConversationAsReadManual(conversationId, userId) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) {
    throw new Error('Conversation not found');
  }
  
  // Update the manuallyMarkedUnread field to remove this user
  // This is used in conjunction with the actual read status
  await updateDoc(docRef, {
    [`manuallyMarkedUnread.${userId}`]: false
  });
}

/**
 * Mark a conversation as unread for a user (manually)
 */
export async function markConversationAsUnread(conversationId, userId) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId);
  await updateDoc(docRef, {
    [`manuallyMarkedUnread.${userId}`]: true
  });
}

/**
 * Update group name
 */
export async function updateGroupName(conversationId, newName, changedByUser) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId);
  await updateDoc(docRef, {
    name: newName,
    updatedAt: serverTimestamp()
  });
  
  // Add system message
  await addSystemMessage(conversationId, {
    subtype: SYSTEM_MESSAGE_TYPES.GROUP_RENAMED,
    text: `${changedByUser.name || 'Someone'} changed the group name to "${newName}"`,
    userId: changedByUser.id
  });
}

/**
 * Add a participant to a group conversation
 */
export async function addParticipant(conversation, newUser, addedByUser) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversation.id);
  
  // Check if current name is auto-generated (order-independent)
  const isAutoName = isAutoGeneratedName(conversation.name, conversation.participantDetails || {});
  
  const newDetails = {
    name: newUser.name || newUser.email,
    avatar: sanitizeAvatarUrl(newUser.avatar)
  };
  
  const updateData = {
    participants: arrayUnion(newUser.id),
    [`participantDetails.${newUser.id}`]: newDetails,
    updatedAt: serverTimestamp()
  };
  
  // Only update name if it was auto-generated
  if (isAutoName) {
    updateData.name = buildAutoName({
      ...conversation.participantDetails,
      [newUser.id]: newDetails
    });
  }
  
  await updateDoc(docRef, updateData);
  
  // Add system message
  await addSystemMessage(conversation.id, {
    subtype: SYSTEM_MESSAGE_TYPES.USER_JOINED,
    text: `${addedByUser.name || 'Someone'} added ${newUser.name || 'someone'}`,
    userId: addedByUser.id
  });
}

/**
 * Remove a participant (leave group)
 */
export async function leaveConversation(conversation, user) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversation.id);
  
  // Check if current name is auto-generated (order-independent)
  const isAutoName = isAutoGeneratedName(conversation.name, conversation.participantDetails || {});
  
  const updateData = {
    participants: arrayRemove(user.id),
    updatedAt: serverTimestamp()
  };
  
  // Only update name if it was auto-generated
  if (isAutoName) {
    updateData.name = buildAutoName(conversation.participantDetails, user.id);
  }

  // R19 (DIV-019): post the system message BEFORE leaving. The message-create
  // rule requires the sender to still be a participant, so the old order
  // (leave, then announce) was denied by Firestore once the leave itself works.
  await addSystemMessage(conversation.id, {
    subtype: SYSTEM_MESSAGE_TYPES.USER_LEFT,
    text: `${user.name || 'Someone'} left the group`,
    userId: user.id
  });

  await updateDoc(docRef, updateData);
}

/**
 * Remove a participant from a group (kicked by admin/creator)
 */
export async function removeParticipant(conversationId, userToRemove, removedByUser) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId);
  
  // Get current conversation to check permissions
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) {
    throw new Error('Conversation not found');
  }
  
  const conversation = snapshot.data();
  
  // Only the creator can remove members
  if (conversation.createdBy !== removedByUser.id) {
    throw new Error('Only the group creator can remove members');
  }
  
  // Can't remove the creator
  if (userToRemove.id === conversation.createdBy) {
    throw new Error('Cannot remove the group creator');
  }
  
  // Check if current name is auto-generated (order-independent)
  const isAutoName = isAutoGeneratedName(conversation.name, conversation.participantDetails || {});
  
  const updateData = {
    participants: arrayRemove(userToRemove.id),
    [`participantDetails.${userToRemove.id}`]: deleteField(),
    updatedAt: serverTimestamp()
  };
  
  // Only update name if it was auto-generated
  if (isAutoName) {
    updateData.name = buildAutoName(conversation.participantDetails, userToRemove.id);
  }
  
  // Remove from participants array and participantDetails
  await updateDoc(docRef, updateData);
  
  // Add system message
  await addSystemMessage(conversationId, {
    subtype: SYSTEM_MESSAGE_TYPES.USER_LEFT,
    text: `${removedByUser.name || 'Someone'} removed ${userToRemove.name || 'someone'} from the group`,
    userId: removedByUser.id
  });
}

/**
 * Extract a friendly name from an email address
 * e.g., "alexonhe+admin1@gmail.com" -> "Alexonhe"
 * e.g., "john.doe@example.com" -> "John Doe"
 */
function getFriendlyNameFromEmail(email) {
  if (!email || !email.includes('@')) return email || 'Unknown';

  // Get local part before @
  let localPart = email.split('@')[0];

  // Remove +suffix (e.g., alexonhe+admin1 -> alexonhe)
  if (localPart.includes('+')) {
    localPart = localPart.split('+')[0];
  }

  // Replace dots and underscores with spaces, capitalize each word
  const friendlyName = localPart
    .replace(/[._]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  return friendlyName;
}

/**
 * Get conversation display name for a user
 * (For direct chats, shows the other person's name)
 * Shows "(no longer a user)" if the participant has been removed
 */
export function getConversationDisplayName(conversation, currentUserId) {
  if (conversation.type === CONVERSATION_TYPES.GROUP) {
    return conversation.name;
  }

  // For direct conversations, find the other participant
  const otherUserId = conversation.participants.find(id => id !== currentUserId);
  const otherUser = conversation.participantDetails?.[otherUserId];
  let baseName = otherUser?.name || 'Unknown';

  // If the name looks like an email, extract a friendly name
  if (baseName && baseName.includes('@')) {
    baseName = getFriendlyNameFromEmail(baseName);
  }

  // Check if this participant has been removed
  const removedParticipants = conversation.removedParticipants || {};
  if (removedParticipants[otherUserId]) {
    return `${baseName} (no longer a user)`;
  }

  return baseName;
}

/**
 * Get conversation avatar for a user
 */
/**
 * Get the other participant's ID for a DM conversation (for avatar caching)
 */
export function getConversationParticipantId(conversation, currentUserId) {
  if (conversation.type === CONVERSATION_TYPES.GROUP) {
    return null; // Groups don't have a single participant
  }
  
  return conversation.participants.find(id => id !== currentUserId) || null;
}
